import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack,
  Visibility,
  VisibilityOff,
  Person,
  Email,
  Lock,
  LockOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { theme as appTheme } from '../../theme/theme';

interface FormData {
  displayName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const RegisterScreen: React.FC = () => {
  const navigate = useNavigate();
  const { signUp, error: authError } = useAuth();
  
  // 监听AuthContext的error状态变化
  useEffect(() => {
    if (authError) {
      setError(authError);
      setShowError(true);
    }
  }, [authError]);
  
  const [formData, setFormData] = useState<FormData>({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);

  const validateForm = () => {
    if (!formData.displayName.trim()) {
      setError('请输入姓名');
      setShowError(true);
      return false;
    }
    if (!formData.email.trim()) {
      setError('请输入邮箱');
      setShowError(true);
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('请输入有效的邮箱地址');
      setShowError(true);
      return false;
    }
    if (formData.password.length < 6) {
      setError('密码至少需要6位字符');
      setShowError(true);
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      setShowError(true);
      return false;
    }
    if (!acceptTerms) {
      setError('请同意服务条款和隐私政策');
      setShowError(true);
      return false;
    }
    return true;
  };

  const handleEmailRegister = async () => {
    if (!validateForm()) return;
    
    try {
      setIsLoading(true);
      await signUp(formData.email, formData.password, formData.displayName);
      // 注册成功，signUp方法会自动导航
    } catch (err: any) {
      // AuthContext会设置错误信息，useEffect会自动显示
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsClick = () => {
    navigate('/legal/terms-of-service');
  };

  const handlePrivacyClick = () => {
    navigate('/legal/privacy-policy');
  };

  const updateFormData = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100vh',
        overflowY: 'auto',
        py: 4,
        background: 'linear-gradient(135deg, var(--primary-purple) 0%, var(--gradient-purple) 50%, var(--primary-purple) 100%)',
        minHeight: '100vh',
      }}
    >
      <Container
        maxWidth="sm"
        sx={{
          width: '100%',
          '&::-webkit-scrollbar': {
            width: '0.4em',
          },
          '&::-webkit-scrollbar-track': {
            boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
            webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,.1)',
            borderRadius: '4px',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 3, sm: 4 }, position: 'relative' }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              position: 'absolute',
              left: { xs: -1, sm: 0 },
              top: 0,
              color: 'white',
              padding: { xs: 1, sm: 1.5 },
              '&:hover': {
                backgroundColor: alpha('#ffffff', 0.1),
              },
            }}
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              mb: 1,
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            }}
          >
            加入紫微AI
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#F5F5DC',
              fontWeight: 300,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
              px: { xs: 2, sm: 0 },
            }}
          >
            创建账户，解锁您的命运密码
          </Typography>
        </Box>

        {/* Registration Form */}
        <Paper
          elevation={8}
          sx={{
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            backgroundColor: '#FFFFFF',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              textAlign: 'center',
              mb: { xs: 2, sm: 3 },
              color: appTheme.colors.primary,
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            }}
          >
            创建账户
          </Typography>

          <TextField
            fullWidth
            label="姓名"
            value={formData.displayName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData('displayName', e.target.value)}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          />

          <TextField
            fullWidth
            label="邮箱"
            type="email"
            value={formData.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData('email', e.target.value)}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          />

          <TextField
            fullWidth
            label="密码"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData('password', e.target.value)}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: { xs: 1.5, sm: 2 } }}
          />

          <TextField
            fullWidth
            label="确认密码"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormData('confirmPassword', e.target.value)}
            variant="outlined"
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined color="primary" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: { xs: 2, sm: 3 } }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptTerms}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAcceptTerms(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                我同意{' '}
                <Typography
                  component="span"
                  onClick={handleTermsClick}
                  sx={{
                    color: appTheme.colors.primary,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  服务条款
                </Typography>
                {' '}和{' '}
                <Typography
                  component="span"
                  onClick={handlePrivacyClick}
                  sx={{
                    color: appTheme.colors.primary,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  隐私政策
                </Typography>
              </Typography>
            }
            sx={{ mb: { xs: 2, sm: 3 }, alignItems: 'flex-start' }}
          />

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleEmailRegister}
            disabled={isLoading}
            sx={{
              mb: { xs: 2, sm: 3 },
              py: { xs: 1.25, sm: 1.5 },
              borderRadius: 3,
              backgroundColor: appTheme.colors.primary,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              '&:hover': {
                backgroundColor: alpha(appTheme.colors.primary, 0.8),
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              '创建账户'
            )}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              component="span"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              已有账户？{' '}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              onClick={() => navigate('/auth/login')}
              sx={{
                color: appTheme.colors.primary,
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                '&:hover': {
                  color: alpha(appTheme.colors.primary, 0.8),
                },
              }}
            >
              立即登录
            </Typography>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegisterScreen;